(() => {
  if (window.AfsBuildCart) return;
  const settings = JSON.parse(document.getElementById('afs-build-cart-settings')?.textContent || '{}');
  const root = window.Shopify?.routes?.root || '/';
  const text = settings.text || {};
  let queue = Promise.resolve();
  const serial = (task) => {
    const result = queue.then(task);
    queue = result.catch(() => {});
    return result;
  };
  const request = async (path, body) => {
    const response = await fetch(root + path, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const result = await response.json();
    if (!response.ok || result.errors) throw new Error(result.description || result.errors || text.failed);
    return result;
  };
  const isMount = (item) => Number(item.product_id) === Number(settings.productId);
  /* Starlink standard mounts (RJ45 / Mini DC) are priced like Build Your Own: the mount is the
     base price and the Starlink connector is its own linked line. The Starlink product page marks
     its mount line with _afs_starlink=1. Their cutout fees are already in the mount price, so only
     the connector line is managed here. Older Starlink lines in a cart (RJ45 included in the mount
     price, no marker) are left completely alone. */
  const isStarlinkMount = (item) => item.properties?._afs_role === 'mount' && item.properties?._afs_starlink === '1';
  const isAnyMount = (item) => isMount(item) || isStarlinkMount(item);
  const feeIds = [Number(settings.cutoutId), Number(settings.starlinkId), Number(settings.starlinkMiniId)].filter(Boolean);
  const isFee = (item) => feeIds.includes(Number(item.variant_id)) && Boolean(item.properties?._afs_setup_id);
  const plan = (cart) => {
    const updates = {}, additions = [], required = new Map();
    for (const mount of cart.items.filter(isAnyMount)) {
      const props = mount.properties || {};
      const starlinkMount = isStarlinkMount(mount);
      const count = starlinkMount ? 1 : Number(props.Cutouts);
      const setup = props._afs_setup_id;
      if (!setup || !Number.isInteger(count) || count < 1 || count > 3) throw new Error(text.invalid);
      const quantities = required.get(setup) || new Map();
      const cutouts = starlinkMount ? 0 : (count - 1) * mount.quantity;
      const configuration = String(props.Configuration || '').split(' / ');
      const starlink = configuration.filter(item => item === 'Starlink RJ45').length * mount.quantity;
      const starlinkMini = configuration.filter(item => item === 'Starlink Mini DC Plug').length * mount.quantity;
      for (const [id, quantity] of [[Number(settings.cutoutId), cutouts], [Number(settings.starlinkId), starlink], [Number(settings.starlinkMiniId), starlinkMini]]) {
        if (quantity && !id) throw new Error(text.failed);
        if (id) quantities.set(id, (quantities.get(id) || 0) + quantity);
      }
      required.set(setup, quantities);
    }
    for (const [setup, quantities] of required) {
      for (const [id, quantity] of quantities) {
        const matches = cart.items.filter(item => Number(item.variant_id) === id && item.properties?._afs_setup_id === setup);
        let remaining = quantity;
        for (const item of matches) {
          const wanted = Math.min(item.quantity, remaining);
          if (item.quantity !== wanted) updates[item.key] = wanted;
          remaining -= wanted;
        }
        if (remaining) additions.push({ id, quantity: remaining, properties: { _afs_setup_id: setup, _afs_role: 'configuration' } });
      }
    }
    for (const item of cart.items) {
      if (isFee(item) && !required.has(item.properties._afs_setup_id)) updates[item.key] = 0;
    }
    return { updates, additions };
  };
  const reconcile = async (cart) => {
    let changed = false;
    for (let attempt = 0; attempt < 3; attempt++) {
      const { updates, additions } = plan(cart);
      if (!Object.keys(updates).length && !additions.length) return { cart, changed };
      changed = true;
      if (Object.keys(updates).length) await request('cart/update.js', { updates });
      if (additions.length) await request('cart/add.js', { items: additions });
      cart = await request('cart.js');
    }
    throw new Error(text.failed);
  };
  const showError = (message) => {
    for (const host of document.querySelectorAll('.cart__ctas')) {
      let error = host.querySelector('[data-afs-build-cart-error]');
      if (!error) {
        error = document.createElement('p');
        error.dataset.afsBuildCartError = '';
        error.setAttribute('role', 'alert');
        host.prepend(error);
      }
      error.textContent = message || text.failed;
    }
  };
  window.AfsBuildCart = {
    plan,
    sync: () => serial(async () => reconcile(await request('cart.js'))),
    change: (body, key) => serial(async () => {
      let cart = await request('cart.js');
      const item = cart.items.find(item => item.key === key);
      if (!item) throw new Error(text.retry);
      const setup = item.properties?._afs_setup_id;
      if (isFee(item) && cart.items.some(mount => isAnyMount(mount) && mount.properties?._afs_setup_id === setup)) {
        const result = await reconcile(cart);
        cart = result.cart;
      } else if (setup && item.properties?._afs_role === 'mount' && body.quantity === 0) {
        const updates = {};
        for (const line of cart.items) if (line.properties?._afs_setup_id === setup) updates[line.key] = 0;
        cart = await request('cart/update.js', { updates });
        cart = (await reconcile(cart)).cart;
      } else {
        cart = await request('cart/change.js', { id: key, quantity: body.quantity });
        cart = (await reconcile(cart)).cart;
      }
      return request('cart/update.js', { updates: {}, sections: body.sections, sections_url: body.sections_url });
    })
  };
  let checkoutBusy = false;
  const checkout = async (form, submitter, href) => {
    if (checkoutBusy) return;
    checkoutBusy = true;
    try {
      const scope = form || document;
      for (const row of scope.querySelectorAll('.cart-items__table-row[data-key]')) {
        const input = row.querySelector('input[name="updates[]"][type="number"]');
        if (!input || input.disabled || input.value === input.defaultValue) continue;
        const quantity = Number(input.value);
        if (!Number.isInteger(quantity) || quantity < 0) throw new Error(text.retry);
        await window.AfsBuildCart.change({ quantity, sections: [], sections_url: window.location.pathname }, row.dataset.key);
      }
      const { changed } = await window.AfsBuildCart.sync();
      if (changed) {
        window.location.href = root + 'cart';
        return;
      }
      if (form) {
        const note = new FormData(form).get('note');
        if (note !== null) await serial(() => request('cart/update.js', { note }));
      }
      window.location.href = href || root + 'checkout';
    } catch (error) {
      showError(error.message);
    } finally {
      checkoutBusy = false;
    }
  };
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
    if (form.dataset.afsCheckoutReady === 'true') {
      delete form.dataset.afsCheckoutReady;
      return;
    }
    if (event.submitter && event.submitter.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout(form, event.submitter);
  }, true);
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || !/\/(checkout|checkouts)(\/|$)/.test(url.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout(null, null, link.href);
  }, true);
  const repair = () => window.AfsBuildCart.sync().then(({ changed }) => {
    if (changed) window.location.reload();
  }).catch(error => showError(error.message));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', repair, { once: true });
  else repair();
  window.addEventListener('pageshow', event => { if (event.persisted) repair(); });
})();